/**
 * QueryBuilder — structured filter/sort/search UI for tabular Parquet previews.
 *
 * Always-visible row: Search input + Apply button + row count.
 * Collapsible "More filters" panel: In columns, Sort by, filter rows, + Add filter.
 */

import {
  ArrowUpDown,
  ChevronDown,
  ChevronUp,
  Plus,
  Search,
  SlidersHorizontal,
  X,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import type {
  TabularColumn,
  TabularFilterCondition,
  TabularQueryRequest,
  TabularSort,
} from '@/services/api/lake'

// ---------------------------------------------------------------------------
// Operator definitions
// ---------------------------------------------------------------------------

type OperatorDef = { value: TabularFilterCondition['operator']; label: string }

const STRING_OPS: OperatorDef[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'contains', label: 'contains' },
  { value: 'starts_with', label: 'starts with' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
]

const NUMBER_OPS: OperatorDef[] = [
  { value: 'eq', label: 'equals' },
  { value: 'neq', label: 'not equals' },
  { value: 'gt', label: '>' },
  { value: 'gte', label: '>=' },
  { value: 'lt', label: '<' },
  { value: 'lte', label: '<=' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
]

const BOOLEAN_OPS: OperatorDef[] = [
  { value: 'eq', label: 'equals' },
  { value: 'is_null', label: 'is empty' },
  { value: 'is_not_null', label: 'is not empty' },
]

const NO_VALUE_OPS = new Set<string>(['is_null', 'is_not_null'])

function getOpsForType(dataType: string): OperatorDef[] {
  if (dataType === 'boolean') return BOOLEAN_OPS
  if (
    dataType === 'integer' ||
    dataType === 'float' ||
    dataType === 'date' ||
    dataType === 'timestamp'
  )
    return NUMBER_OPS
  return STRING_OPS
}

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface FilterRow {
  id: number
  column: string
  operator: TabularFilterCondition['operator']
  value: string
}

interface QueryBuilderProps {
  columns: TabularColumn[]
  onQuery: (req: TabularQueryRequest) => void
  totalRows: number
  filteredRows: number
  isLoading: boolean
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

let _nextId = 1

export function QueryBuilder({
  columns,
  onQuery,
  totalRows,
  filteredRows,
  isLoading,
}: QueryBuilderProps) {
  // Selectable columns: data columns (exclude __row_index) that have a usable
  // name. Messy CSV/Excel headers can yield blank or whitespace-only column
  // names; those can't be referenced in a query and a blank value crashes the
  // Radix Select (it forbids empty-string SelectItem values), so exclude them.
  const dataCols = columns.filter(c => c.name !== '__row_index' && c.name.trim() !== '')

  // Global search state
  const [search, setSearch] = useState('')
  const [searchCols, setSearchCols] = useState<string[]>([])

  // Filter rows state
  const [filters, setFilters] = useState<FilterRow[]>([])

  // Sort state
  const [sort, setSort] = useState<TabularSort | null>(null)

  // "More filters" panel open/closed
  const [expanded, setExpanded] = useState(false)

  // Count of active hidden-filter states (shown as badge on toggle button)
  const hiddenActiveCount = (searchCols.length > 0 ? 1 : 0) + filters.length + (sort ? 1 : 0)

  function addFilter() {
    const firstCol = dataCols[0]
    if (!firstCol) return
    const ops = getOpsForType(firstCol.data_type)
    setFilters(prev => [
      ...prev,
      { id: _nextId++, column: firstCol.name, operator: ops[0].value, value: '' },
    ])
    // Auto-expand when user adds a filter
    setExpanded(true)
  }

  function removeFilter(id: number) {
    setFilters(prev => prev.filter(f => f.id !== id))
  }

  function updateFilter(id: number, patch: Partial<FilterRow>) {
    setFilters(prev =>
      prev.map(f => {
        if (f.id !== id) return f
        const updated = { ...f, ...patch }
        // When column changes, reset operator to first valid one for the new type
        if (patch.column !== undefined) {
          const col = dataCols.find(c => c.name === patch.column)
          const ops = col ? getOpsForType(col.data_type) : STRING_OPS
          updated.operator = ops[0].value
          updated.value = ''
        }
        // When operator changes to no-value op, clear value
        if (patch.operator !== undefined && NO_VALUE_OPS.has(patch.operator)) {
          updated.value = ''
        }
        return updated
      })
    )
  }

  function toggleSearchCol(colName: string) {
    setSearchCols(prev =>
      prev.includes(colName) ? prev.filter(c => c !== colName) : [...prev, colName]
    )
  }

  function handleApply() {
    const request: TabularQueryRequest = {
      filters: filters
        .filter(f => f.column)
        .map(f => ({
          column: f.column,
          operator: f.operator,
          value: NO_VALUE_OPS.has(f.operator) ? undefined : f.value || undefined,
        })),
      sort: sort ?? undefined,
      search: search.trim() || undefined,
      search_columns: searchCols.length > 0 ? searchCols : undefined,
      offset: 0,
    }
    onQuery(request)
  }

  function handleClear() {
    setSearch('')
    setSearchCols([])
    setFilters([])
    setSort(null)
    onQuery({ offset: 0 })
  }

  const hasActiveFilters = !!(search.trim() || filters.length > 0 || sort)

  return (
    <div className="border-b bg-muted/30">
      {/* ── Always-visible row: Search + More filters toggle + Apply + count ── */}
      <div className="flex gap-2 items-end px-3 pt-3 pb-3 max-md:flex-wrap">
        {/* Global search */}
        <div className="flex-1 min-w-0 max-md:basis-full">
          <Label className="text-xs text-muted-foreground mb-1 block">Search</Label>
          <div className="relative">
            <Search className="absolute left-2 top-2 h-3.5 w-3.5 text-muted-foreground" />
            <Input
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search across columns…"
              className="pl-7 h-8 text-sm"
              onKeyDown={e => e.key === 'Enter' && handleApply()}
            />
          </div>
        </div>

        {/* More filters toggle */}
        <div className="self-end">
          <Button
            variant="outline"
            size="sm"
            className="h-8 text-xs gap-1.5"
            onClick={() => setExpanded(v => !v)}
          >
            {expanded ? (
              <ChevronUp className="h-3.5 w-3.5" />
            ) : (
              <ChevronDown className="h-3.5 w-3.5" />
            )}
            More filters
            {!expanded && hiddenActiveCount > 0 && (
              <Badge className="ml-0.5 h-4 min-w-4 px-1 text-[10px] bg-primary text-primary-foreground">
                {hiddenActiveCount}
              </Badge>
            )}
          </Button>
        </div>

        {/* Apply button */}
        <div className="self-end">
          <Button size="sm" className="h-8 text-xs" onClick={handleApply} disabled={isLoading}>
            <SlidersHorizontal className="h-3 w-3 mr-1" />
            Apply
          </Button>
        </div>

        {/* Row count */}
        <span className="text-xs text-muted-foreground self-end pb-1.5 whitespace-nowrap max-md:ml-auto">
          {isLoading
            ? 'Loading…'
            : `${filteredRows.toLocaleString()} of ${totalRows.toLocaleString()} rows`}
        </span>
      </div>

      {/* ── Collapsible "More filters" panel ── */}
      {expanded && (
        <div className="border-t px-3 pt-3 pb-3 space-y-3">
          {/* In columns + Sort by row */}
          <div className="flex gap-2 items-end max-md:flex-wrap">
            {/* Search column selector */}
            {dataCols.length > 0 && (
              <div className="">
                <Label className="text-xs text-muted-foreground mb-1 block">In columns</Label>
                <Select
                  value={searchCols.length === 0 ? '__all__' : searchCols[searchCols.length - 1]}
                  onValueChange={val => {
                    if (val === '__all__') {
                      setSearchCols([])
                    } else {
                      toggleSearchCol(val)
                    }
                  }}
                >
                  <SelectTrigger className="h-8 text-sm">
                    <SelectValue placeholder="All columns" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="__all__">All columns</SelectItem>
                    {dataCols.map((c, i) => (
                      <SelectItem key={`${c.name}-${i}`} value={c.name}>
                        <span className={searchCols.includes(c.name) ? 'font-medium' : ''}>
                          {searchCols.includes(c.name) ? '✓ ' : ''}
                          {c.name}
                        </span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}

            {/* Sort by */}
            <div className="">
              <Label className="text-xs text-muted-foreground mb-1 block">Sort by</Label>
              <Select
                value={sort ? sort.column : '__none__'}
                onValueChange={val => {
                  if (val === '__none__') setSort(null)
                  else
                    setSort(s => ({
                      column: val,
                      direction:
                        s?.column === val ? (s.direction === 'asc' ? 'desc' : 'asc') : 'asc',
                    }))
                }}
              >
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue placeholder="None" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="__none__">None</SelectItem>
                  {dataCols.map((c, i) => (
                    <SelectItem key={`${c.name}-${i}`} value={c.name}>
                      {c.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Sort direction toggle */}
            {sort && (
              <Button
                variant="outline"
                size="sm"
                className="h-8 px-2 self-end"
                onClick={() =>
                  setSort(s =>
                    s ? { ...s, direction: s.direction === 'asc' ? 'desc' : 'asc' } : null
                  )
                }
                title={
                  sort.direction === 'asc'
                    ? 'Ascending — click for descending'
                    : 'Descending — click for ascending'
                }
              >
                <ArrowUpDown className="h-3.5 w-3.5 mr-1" />
                {sort.direction === 'asc' ? 'ASC' : 'DESC'}
              </Button>
            )}
          </div>

          {/* Filter rows */}
          {filters.length > 0 && (
            <div className="space-y-1.5">
              {filters.map(f => {
                const col = dataCols.find(c => c.name === f.column)
                const ops = col ? getOpsForType(col.data_type) : STRING_OPS
                const noVal = NO_VALUE_OPS.has(f.operator)
                return (
                  <div key={f.id} className="flex gap-1.5 items-center max-md:flex-wrap">
                    {/* Column */}
                    <Select
                      value={f.column}
                      onValueChange={val => updateFilter(f.id, { column: val })}
                    >
                      <SelectTrigger className="h-7 text-xs w-36 flex-shrink-0 max-md:min-w-0 max-md:flex-1 max-md:shrink">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {dataCols.map((c, i) => (
                          <SelectItem key={`${c.name}-${i}`} value={c.name} className="text-xs">
                            {c.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Operator */}
                    <Select
                      value={f.operator}
                      onValueChange={val =>
                        updateFilter(f.id, { operator: val as TabularFilterCondition['operator'] })
                      }
                    >
                      <SelectTrigger className="h-7 text-xs w-28 flex-shrink-0">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {ops.map(op => (
                          <SelectItem key={op.value} value={op.value} className="text-xs">
                            {op.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>

                    {/* Value */}
                    {!noVal && (
                      <Input
                        value={f.value}
                        onChange={e => updateFilter(f.id, { value: e.target.value })}
                        placeholder="value"
                        className="h-7 text-xs flex-1 min-w-0 max-md:order-last max-md:basis-full"
                        onKeyDown={e => e.key === 'Enter' && handleApply()}
                      />
                    )}
                    {noVal && <div className="flex-1 max-md:hidden" />}

                    {/* Remove */}
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7 flex-shrink-0"
                      onClick={() => removeFilter(f.id)}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )
              })}
            </div>
          )}

          {/* Add filter + Clear all row */}
          <div className="flex gap-2 items-center">
            <Button
              variant="ghost"
              size="sm"
              className="h-7 text-xs text-muted-foreground"
              onClick={addFilter}
              disabled={dataCols.length === 0}
            >
              <Plus className="h-3 w-3 mr-1" />
              Add filter
            </Button>

            <div className="flex-1" />

            {hasActiveFilters && (
              <Button variant="ghost" size="sm" className="h-7 text-xs" onClick={handleClear}>
                Clear all
              </Button>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
